import React, { useState, useEffect } from 'react';
import './DpeUpload.css';
import {
  Box,
  Button,
  Heading,
  IconButton,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  Tooltip,
  Stack,
} from '@chakra-ui/core';
import { Formik, useField } from 'formik';
import { useDispatch } from 'react-redux';
import { FileUploader } from "react-drag-drop-files";
import { PrimaryButton, SecondaryButton } from '../Buttons';
import { ErrorMessage } from '../Form/FormInputs';
import { toastActions } from '../../../redux/toast/actions';
import { TOAST_MESSAGE } from '../Toasts/Toast';
import { blocAPI } from '../../api/blocs';

const DpeUpload = ({ folder, successCallback, isDisabled = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isConfirmationOpen,
    onOpen: onConfirmationOpen,
    onClose: onConfirmationClose,
  } = useDisclosure();

  const { isOpen: isSuccessOpen, onOpen: onSuccessOpen, onClose: onSuccessClose } = useDisclosure();

  const dispatch = useDispatch();


  const needCompleteDpePDF = [
    'AMOc - diagnostic conjoint',
    'AMOc - diagnostic ergo seul',
    'AMOc - diagnostic devis existant',
    'AMOc - visite conjointe',
    'AMOc - ergo seul',
    'AMOc - devis existant',
    'AMOc - facture existante',
    'AMOe - AMD',
    'AMOe - visite diagnostic',
    'AMOc - DAL + 2DPE – Seule',
    'AMOc - DAL + 2DPE – Conjointe',
    'AMOc - DAL + 2DPE – sur devis',
  ].includes(folder?.ergoInterventionType);

  const needCompleteDpeWord = [
    'AMOc - visite conjointe',
    'AMOc - ergo seul',
    'AMOc - devis existant',
    'AMOe - visite diagnostic',
    'AMOc - DAL + 2DPE – Seule',
    'AMOc - DAL + 2DPE – Conjointe',
    'AMOc - DAL + 2DPE – sur devis',
  ].includes(folder?.ergoInterventionType);

  const needWorkRecommendationsPDF = [
    'AMOc - visite conjointe',
    'AMOc - ergo seul',
    'AMOc - devis existant',
    'AMOc - DAL + 2DPE – Seule',
    'AMOc - DAL + 2DPE – Conjointe',
    'AMOc - DAL + 2DPE – sur devis',
  ].includes(folder?.ergoInterventionType);

  const needWorkRecommendationsWord = [
    'AMOc - visite conjointe',
    'AMOc - ergo seul',
    'AMOc - devis existant',
    'AMOc - DAL + 2DPE – Seule',
    'AMOc - DAL + 2DPE – Conjointe',
    'AMOc - DAL + 2DPE – sur devis',
  ].includes(folder?.ergoInterventionType);

  const needHousingAutonomyDiagnostic = [
    'AMOc - DAL + 2DPE – Seule',
    'AMOc - DAL + 2DPE – Conjointe',
    'AMOc - DAL + 2DPE – sur devis',
  ].includes(folder?.ergoInterventionType);

  const DPE_INPUT_CONFIG = {
    complete: {
      id: "completeDpeFile",
      name: "completeDpeFile",
      uploadType: "complete",
      label: "Compte-rendu complet",
      placeholder: "Aucun compte-rendu complet disponible. Déposez un fichier PDF."
    },
    workRecommendations: {
      id: "workRecommendationsFile",
      name: "workRecommendationsFile",
      uploadType: "workRecommendations",
      label: "Préconisations de travaux",
      placeholder: "Aucune préconisation de travaux disponible. Déposez un fichier PDF."
    },
    allRecommendations: {
      id: "allRecommendationsFile",
      name: "allRecommendationsFile",
      uploadType: "allRecommendations",
      label: "Toutes les préconisations",
      placeholder: "Aucune préconisation disponible. Déposez un fichier PDF."
    },
    word: {
      id: "wordFormatFile",
      name: "wordFormatFile",
      uploadType: "word",
      label: "Format Word",
      acceptFormat: ".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      placeholder: "Aucun compte-rendu disponible. Déposez un fichier Word."
    },
    housingAutonomyDiagnostic: {
      id: "housingAutonomyDiagnosticFile",
      name: "housingAutonomyDiagnosticFile",
      uploadType: "housingAutonomyDiagnostic",
      label: "Diagnostic autonomie logement",
      placeholder: "Aucun diagnostic autonomie logement (DAL) disponible. Déposez un fichier PDF."
    },
  };

  const disallowUpload = !folder || isDisabled;

  const onModalClose = formik => {
    formik.resetForm();
    onClose();
  };

  const onSuccessModalClose = () => {
    onSuccessClose();
    if (successCallback) {
      successCallback();
    }
  };

  const upload = async values => {
    const formData = new FormData();

    // Add each file that exists with its type
    if (values.completeDpeFile) {
      formData.append('files[][file]', values.completeDpeFile);
      formData.append('files[][type]', 'complete');
    }
    if (values.workRecommendationsFile) {
      formData.append('files[][file]', values.workRecommendationsFile);
      formData.append('files[][type]', 'workRecommendations');
    }
    if (values.allRecommendationsFile) {
      formData.append('files[][file]', values.allRecommendationsFile);
      formData.append('files[][type]', 'allRecommendations');
    }
    if (values.wordFormatFile) {
      formData.append('files[][file]', values.wordFormatFile);
      formData.append('files[][type]', 'word');
    }
    if (values.housingAutonomyDiagnosticFile) {
      formData.append('files[][file]', values.housingAutonomyDiagnosticFile);
      formData.append('files[][type]', 'housingAutonomyDiagnostic');
    }

    return blocAPI.uploadDpeFile(folder.id, formData);
  };

  const isFormValid = (values) => {
    const requiredFields = [];

    if (needCompleteDpePDF) {
      requiredFields.push('completeDpeFile');
    }
    if (needCompleteDpeWord) {
      requiredFields.push('wordFormatFile');
    }
    if (needWorkRecommendationsPDF) {
      requiredFields.push('workRecommendationsFile');
    }
    if (needWorkRecommendationsWord) {
      requiredFields.push('allRecommendationsFile');
    }
    if (needHousingAutonomyDiagnostic) {
      requiredFields.push('housingAutonomyDiagnosticFile');

    }

    return requiredFields.length === 0 || requiredFields.every(field => values[field]);
  };

  return (
    <>
      <Tooltip
        zIndex={1000}
        bg="semiDarkGray.900"
        aria-label="Mise en ligne d'un compte-rendu DPE"
        label="Mise en ligne d'un compte-rendu DPE"
        placement="top"
      >
        <PrimaryButton
          onClick={onOpen}
          isDisabled={disallowUpload}
          mb={8}
        >
          Téléverser une nouvelle verison du compte-rendu
        </PrimaryButton>
      </Tooltip>

      <SuccessModal
        isOpen={isSuccessOpen}
        onClose={onSuccessModalClose}
        onOpen={onSuccessOpen}
        successCallback={successCallback}
      />

      <Formik
        initialValues={{
          completeDpeFile: null,
          workRecommendationsFile: null,
          allRecommendationsFile: null,
          wordFormatFile: null
        }}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={async (values, { setTouched, setErrors, resetForm }) => {
          return upload(values)
            .then(() => {
              setTouched({});
              onModalClose({ resetForm });
              onSuccessOpen();
            })
            .catch(err => {
              dispatch(toastActions.error(TOAST_MESSAGE.error.dpeUpload));
              if (err?.response?.data) {
                if (typeof err?.response?.data.errors === 'object') {
                  setErrors(err?.response?.data.errors);
                } else {
                  setErrors({ dpeFile: err?.response?.data.errors });
                }
              } else {
                console.error(err);
              }
            });
        }}
      >
        {formik => (
          <>
            <ConfirmationModal
              isOpen={isConfirmationOpen}
              onOpen={onConfirmationOpen}
              onClose={onConfirmationClose}
              onSubmit={() => {
                onConfirmationClose();
                formik.submitForm();
              }}
            />
            <Modal isOpen={isOpen} onClose={() => onModalClose(formik)} size="full">
              <ModalOverlay />
              <ModalContent rounded="lg" overflow="hidden" maxWidth="60rem" my="5vh">
                <>
                  <ModalHeader as="div" bg="darkGray" color="white">
                    <Heading as="h2" fontSize="xl" fontWeight="bold">
                      <Text
                        as="span"
                        fontFamily="heading"
                        opacity="0.5"
                        textTransform="uppercase"
                        fontWeight="600"
                      >
                        Dossier n°{' '}
                      </Text>
                      {folder.formattedId}{' '}
                      <Text as="span" d="inline-block" mx={2} opacity="0.65">
                        -
                      </Text>{' '}
                      <Text mt={2} as="small">
                        {folder.recipientFullName || '-'}
                      </Text>
                    </Heading>
                  </ModalHeader>
                  <ModalCloseButton color="white" _focus={{ shadow: 'none' }} />
                  <ModalBody py={5}>
                    <form
                      id="dpeUploadForm"
                      onSubmit={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (folder) {
                          onConfirmationOpen();
                        } else {
                          formik.handleSubmit(e);
                        }
                      }}
                    >
                      {(() => {
                        const inputs = [];

                        if (needCompleteDpePDF) {
                          inputs.push(
                            <DpeUploadInput key="complete" {...DPE_INPUT_CONFIG.complete} folder={folder} />
                          );
                        }

                        if (needWorkRecommendationsPDF) {
                          inputs.push(
                            <DpeUploadInput key="workRec" {...DPE_INPUT_CONFIG.workRecommendations} folder={folder} />
                          );
                        }

                        if (needWorkRecommendationsWord) {
                          inputs.push(
                            <DpeUploadInput key="allRec" {...DPE_INPUT_CONFIG.allRecommendations} folder={folder} />
                          );
                        }

                        if (needCompleteDpeWord) {
                          inputs.push(
                            <DpeUploadInput key="word" {...DPE_INPUT_CONFIG.word} folder={folder} />
                          );
                        }

                        if (needHousingAutonomyDiagnostic) {
                          inputs.push(
                            <DpeUploadInput key="housingAutonomy" {...DPE_INPUT_CONFIG.housingAutonomyDiagnostic} folder={folder} />
                          );
                        }

                        if (inputs.length === 0) {
                          return (
                            <DpeUploadInput
                              id="dpeFile"
                              name="dpeFile"
                              folder={folder}
                              uploadType="complete"
                              label="Compte-rendu complet"
                            />
                          );
                        }

                        return <Stack spacing={4}>{inputs}</Stack>;
                      })()}
                    </form>
                  </ModalBody>
                  <ModalFooter pt={0}>
                    <SecondaryButton type="button" mr={3} onClick={() => onModalClose(formik)}>
                      Annuler
                    </SecondaryButton>
                    <PrimaryButton
                      type="submit"
                      disabled={disallowUpload || !isFormValid(formik.values)}
                      isLoading={formik.isSubmitting}
                      loadingText="Envoi en cours"
                      form="dpeUploadForm"
                    >
                      Envoyer
                    </PrimaryButton>
                  </ModalFooter>
                </>
              </ModalContent>
            </Modal>
          </>
        )}
      </Formik>
    </>
  );
};

export default DpeUpload;

export const DpeUploadInput = ({ uploadType, label, acceptFormat = "application/pdf", ...props }) => {
  const [field, meta, helpers] = useField(props);
  const [fileName, setFileName] = useState('');
  const [previewURL, setPreviewURL] = useState('');

  const handleChange = (file) => {
    if (!file) return;
    helpers.setError(null);
    setFileName(file.name);
    // Only set preview URL for PDF files
    if (file.type === 'application/pdf') {
      setPreviewURL(`${URL.createObjectURL(file)}#toolbar=0`);
    } else {
      setPreviewURL('');
    }
    helpers.setValue(file);
  };

  // Convert acceptFormat to array of extensions for FileUploader
  const fileTypes = [...new Set(acceptFormat.split(',').map(format => {
    if (format.startsWith('.')) {
      return format.slice(1).toUpperCase(); // Remove dot and uppercase
    }
    if (format === 'application/pdf') {
      return 'PDF';
    }
    if (format === 'application/msword') {
      return 'DOC';
    }
    if (format === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return 'DOCX';
    }
    return format.toUpperCase();
  }))];

  return (
    <>
      <DpeUploadWrapper mb={4}>
        <Box minH={50}>
          <DpeUploadFilename
            fileName={fileName}
            folder={props.folder}
            acceptFormat={acceptFormat}
            uploadName={props.placeholder}
          />
          {meta.touched && meta.error && <ErrorMessage p={0}>{meta.error}</ErrorMessage>}
        </Box>
        <FileUploader
          name={props.id || field.name}
          types={fileTypes}
          handleChange={handleChange}
          onBlur={field.onBlur}
          uploadedLabel="Fichier sélectionné"
          label="Sélectionner ou glisser-déposer un fichier"
          classes="dragndrop"
        />
      </DpeUploadWrapper>
      {previewURL && (
        <Box
          as="embed"
          src={previewURL}
          height="500px"
          width="100%"
          mb={4}
          borderRadius="md"
        />
      )}
    </>
  );
};

export const DpeUploadWrapper = ({ children, ...props }) => {
  return (
    <Box bg="lightGray4" rounded="10px" p={6} {...props}>
      {children}
    </Box>
  );
};

export const DpeUploadFilename = ({
  fileName,
  acceptFormat = "application/pdf",
  placeholder,
  ...props
}) => {
  const getPlaceholder = () => {
    return props.uploadName;
  };

  return (
    <Text color={fileName ? null : 'placeholder'}>
      {fileName || placeholder || getPlaceholder()}
    </Text>
  );
};

const ConfirmationModal = ({ isOpen, onSubmit, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay zIndex={2000} />
      <ModalContent rounded="lg" zIndex={2000}>
        <ModalHeader>Confirmation d'envoi de compte-rendu</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <p>
            Attention cette action est irréversible, êtes-vous sûr de vouloir écraser ce compte-rendu ?
          </p>
        </ModalBody>

        <ModalFooter>
          <SecondaryButton mr={3} onClick={onClose}>
            Non
          </SecondaryButton>
          <PrimaryButton backgroundColor="error" onClick={onSubmit}>
            Oui
          </PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const SuccessModal = ({ isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent rounded="lg">
        <ModalHeader>Confirmation de l'envoi</ModalHeader>
        <ModalBody>
          <p>Votre compte-rendu a bien été envoyé.</p>
        </ModalBody>
        <ModalFooter>
          <PrimaryButton onClick={onClose}>OK</PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
